import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { EditIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { b2cStakeholderTypes, stakeholderTypeTitle } from '../../../../types/stakeholder.types';
import { openCompanyStorageUrlInNewTab } from '../../../../utils/company-storage-url.util';
import { ChildrenFormProps, EditStakeholderVariant } from '../stakeholder-actions.types';

export type EditPreviewProps = {
  onEditClick: (step: EditStakeholderVariant) => void;
  previewMode?: boolean;
} & ChildrenFormProps;

export const EditPreview: FC<EditPreviewProps> = ({
  handleCloseModal,
  formData,
  onEditClick,
  previewMode,
  isLoading,
  stakeholder,
}) => {
  const { stepOne, stepTwo } = formData();
  const companyId = stakeholder?.company?.id;

  const handleDocumentClick = (link: string) => {
    if (!companyId) return;
    void openCompanyStorageUrlInNewTab(companyId, link);
  };

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <div className="flex w-full justify-between px-4">
            <span className="text-sm font-[550] text-gray-700">
              <AppFormattedMessage id="GENERAL" />
            </span>
            {!previewMode && (
              <Button className="w-fit" onClick={() => onEditClick(1)} styleType="NONE">
                <EditIcon iconColor="#2565C8" />
              </Button>
            )}
          </div>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            <SummaryItem
              className="pt-0"
              title={<AppFormattedMessage id="STAKEHOLDER_NAME" />}
              value={<Tooltip content={stepOne?.fullName}> {stepOne?.fullName}</Tooltip>}
            />

            <div className="flex flex-col gap-3 pb-3">
              <SummaryItem
                className="pb-0"
                title={<AppFormattedMessage id="EMAIL_MAIN" />}
                value={stepOne?.email}
              />
              {stepOne?.subEmails?.map(({ value: subEmail }) => (
                <SummaryItem
                  className="py-0"
                  key={subEmail}
                  title={<AppFormattedMessage id="SUB_EMAIL_PLACEHOLDER" />}
                  value={subEmail}
                />
              ))}
            </div>

            <SummaryItem
              title={<AppFormattedMessage id="STAKEHOLDER_TYPE" />}
              value={
                <AppFormattedMessage
                  id={
                    stepOne?.stakeholderType
                      ? stakeholderTypeTitle[stepOne?.stakeholderType]
                      : stakeholderTypeTitle.founder
                  }
                />
              }
            />
            {b2cStakeholderTypes.includes(stepOne?.stakeholderType) ? (
              <>
                <SummaryItem
                  title={<AppFormattedMessage id="COUNTRY_OF_COMPANY" />}
                  value={stepOne?.company?.incorporationCountry?.name || '-'}
                />
                <SummaryItem
                  title={<AppFormattedMessage id="COMPANY_IDENTIFIER" />}
                  value={stepOne?.company?.identifier || '-'}
                />
                <SummaryItem
                  title={<AppFormattedMessage id="DATE_OF_INCORPORATION" />}
                  value={
                    stepOne?.company?.incorporationDate
                      ? `${stepOne?.company?.incorporationDate?.day}/${stepOne?.company?.incorporationDate?.month + 1}/${stepOne?.company?.incorporationDate?.year}`
                      : '-'
                  }
                />
              </>
            ) : (
              <>
                <SummaryItem
                  title={<AppFormattedMessage id="PASSPORT_NO" />}
                  value={stepOne?.human?.passportNo || '-'}
                />
                <SummaryItem
                  title={<AppFormattedMessage id="PASSPORT_EXPIRY_DATE" />}
                  value={
                    stepOne?.human?.passportExpiryDate
                      ? `${stepOne?.human?.passportExpiryDate?.day}/${stepOne?.human?.passportExpiryDate?.month + 1}/${stepOne?.human?.passportExpiryDate?.year}`
                      : '-'
                  }
                />
                <SummaryItem
                  title={<AppFormattedMessage id="NATIONALITY" />}
                  value={stepOne?.human?.nationality?.name || '-'}
                />
                <SummaryItem
                  title={<AppFormattedMessage id="BIRTHDAY" />}
                  value={
                    stepOne?.human?.birthdayDate
                      ? `${stepOne?.human?.birthdayDate?.day}/${stepOne?.human?.birthdayDate?.month + 1}/${stepOne?.human?.birthdayDate?.year}`
                      : '-'
                  }
                />
              </>
            )}
            <SummaryItem
              title={<AppFormattedMessage id="PHONE_NUMBER" />}
              value={
                stepOne?.phoneCountry && stepOne?.phoneNumber
                  ? `${stepOne?.phoneCountry.prefix} ${stepOne?.phoneNumber}`
                  : '-'
              }
            />
            <SummaryItem
              className="pb-0"
              title={<AppFormattedMessage id="ADDRESS" />}
              value={stepOne?.address}
            />
          </div>
        </div>

        <div className="flex w-full items-center justify-between rounded-lg border border-gray-100 bg-white px-4">
          <div className={'flex gap-3 text-nowrap py-2'}>
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              <AppFormattedMessage id="DOCUMENTS" />:
            </span>
            <div className="flex max-w-[220px] flex-col gap-2">
              {(stepTwo?.files?.length || 0) > 0 ? (
                stepTwo?.files?.map(({ doc, docLink }) => (
                  <div
                    className={twMerge(
                      'truncate text-xs font-[450] text-gray-700',
                      previewMode && 'cursor-pointer text-brand-700 underline',
                    )}
                    key={docLink}
                    onClick={() => previewMode && handleDocumentClick(docLink)}
                  >
                    {doc?.name}
                  </div>
                ))
              ) : (
                <span className="truncate text-xs font-[450] text-gray-700">-</span>
              )}
            </div>
          </div>
          {!previewMode && (
            <Button className="w-fit" onClick={() => onEditClick(2)} styleType="NONE">
              <EditIcon iconColor="#2565C8" />
            </Button>
          )}
        </div>
        <div className="flex w-full items-start justify-between rounded-lg border border-gray-100 bg-white px-4 py-2">
          <div className="flex w-full gap-3">
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              {<AppFormattedMessage id="ADDITIONAL_NOTES" />}:
            </span>
            <div
              className={twMerge(
                'max-w-[250px] break-words text-xs font-[450] text-gray-700',
                !previewMode ? 'max-w-[220px]' : 'max-w-[250px]',
              )}
            >
              {stepTwo?.additionalNotes || '-'}
            </div>
          </div>
          {!previewMode && (
            <Button className="w-fit" onClick={() => onEditClick(2)} styleType="NONE">
              <EditIcon iconColor="#2565C8" />
            </Button>
          )}
        </div>
      </div>

      <div
        className={twMerge(
          'flex h-9 w-full justify-start gap-3 px-6',
          previewMode && 'justify-end',
        )}
      >
        <Button
          className={twMerge(
            'w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700',
            previewMode && 'bg-brand-700 text-white',
          )}
          isLoading={isLoading}
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          {previewMode ? <AppFormattedMessage id="CLOSE" /> : <AppFormattedMessage id="CANCEL" />}
        </Button>
      </div>
    </>
  );
};
